import { useEffect, useState, useMemo } from "react";
import { IHosting, PriceData } from "../../interface/hosting";
import { 
    Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, Typography, Box, Chip 
} from "@mui/material";

export default function HostingCostSummary({
    hosting,
    type,
    prices,
    isCompared
}: {
    hosting: IHosting,
    type: string,
    prices: PriceData[],
    isCompared: boolean,
}) {
    // FONCTION DE CALCUL UNIQUE ET SÉCURISÉE
    const calculateTotalHT = (priceSource: PriceData): number => {
        if (!priceSource) return 0;
        
        const isDouble = type === "double";
        const baseHours = 720;
        
        let serversTotal = 0;
        if (type !== "personnalisée") {
            const c34 = Number(priceSource.c34) || 0;
            const c38 = Number(priceSource.c38) || 0;
            serversTotal = isDouble ? (c34 * 2 + c38 * 2) : (c34 + c38);
        } else {
            serversTotal = (hosting.custom?.servers || []).reduce((total, server) => {
                const priceKey = server.type as keyof PriceData;
                const unitPrice = Number(priceSource[priceKey]) || 0;
                
                if (server.type === "nfs") {
                    return total + (Number(priceSource.nfs) || 0) * (server.storage || 0);
                }
                return total + unitPrice;
            }, 0);
        }

        const ipPrice = Number(priceSource.floatingIP) || 0;
        const routerPrice = Number(priceSource.router) || 0;
        const backupPrice = Number(priceSource.backup) || 0;
        const margin = Number(hosting.margin) || 0;

        const ipCount = type !== "personnalisée" ? (isDouble ? 4 : 2) : (hosting.custom?.nbIP || 0);
        const backupTotal = (hosting.backup?.storage || 0) * backupPrice;

        const result = baseHours * (serversTotal + (ipPrice * ipCount) + routerPrice + backupTotal) * (1 + margin / 100);
        return isNaN(result) ? 0 : result;
    };

    // Calcul du prix de base (Hébergement actuel)
    const currentTotalHT = useMemo(() => 
        calculateTotalHT(hosting.prices as PriceData), 
    [hosting]);

    // État pour le prix comparé (OVH ou autre source)
    const [totalHT, setTotalHT] = useState<number>(0);

    useEffect(() => {
        const priceSource = isCompared && prices.length > 0 
            ? { ...prices[0] } 
            : { ...hosting.prices };
        setTotalHT(calculateTotalHT(priceSource as PriceData));
    }, [isCompared, hosting, prices]);

    // Style dynamique pour la comparaison
    const getCompareStyle = (current: number, compare: number) => {
        if (!isCompared) return { color: "#1e293b", fontWeight: 600 };
        const diff = Math.round(compare * 100) - Math.round(current * 100);
        if (diff > 0) return { color: "#d32f2f", fontWeight: 700 }; // Rouge
        if (diff < 0) return { color: "#2e7d32", fontWeight: 700 }; // Vert
        return { color: "#1e293b", fontWeight: 600 };
    };

    const renderPriceCell = (current: number, compare: number) => (
        <Typography variant="body2" sx={getCompareStyle(current, compare)}>
            {compare.toFixed(4)} €
        </Typography>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell colSpan={3} sx={{ py: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={800} color="primary">SERVEURS</Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Instance</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Type</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#64748b' }}>Prix / h</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(type === "simple" || type === "double") ? (
                            <>
                                <TableRow>
                                    <TableCell>SRV-LB-1 {type === "double" && "& 2"}</TableCell>
                                    <TableCell>c3-4</TableCell>
                                    <TableCell align="right">
                                        {renderPriceCell(hosting.prices.c34, isCompared && prices[0] ? prices[0].c34 : hosting.prices.c34)}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>SRV-CLT-1 {type === "double" && "& 2"}</TableCell>
                                    <TableCell>c3-8</TableCell>
                                    <TableCell align="right">
                                        {renderPriceCell(hosting.prices.c38, isCompared && prices[0] ? prices[0].c38 : hosting.prices.c38)}
                                    </TableCell>
                                </TableRow>
                            </>
                        ) : (
                            (hosting.custom?.servers || []).map((s, i) => (
                                <TableRow key={i}>
                                    <TableCell>{s.instance}</TableCell>
                                    <TableCell>{s.type.toUpperCase()}</TableCell>
                                    <TableCell align="right">
                                        {renderPriceCell(
                                            (hosting.prices[s.type as keyof typeof hosting.prices] as number) || 0, 
                                            isCompared && prices[0] 
                                                ? (prices[0][s.type as keyof PriceData] as number) || 0 
                                                : (hosting.prices[s.type as keyof typeof hosting.prices] as number) || 0
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>

                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ py: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight={800} color="primary">RÉSEAU & BACKUP</Typography>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>IP Flottantes</TableCell>
                            <TableCell>{type !== "personnalisée" ? (type === "simple" ? "2 IPs" : "4 IPs") : `${hosting.custom.nbIP} IPs`}</TableCell>
                            <TableCell align="right">
                                {renderPriceCell(hosting.prices.floatingIP, isCompared && prices[0] ? prices[0].floatingIP : hosting.prices.floatingIP)}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={2}>Routeur Public</TableCell>
                            <TableCell align="right">
                                {renderPriceCell(hosting.prices.router, isCompared && prices[0] ? prices[0].router : hosting.prices.router)}
                            </TableCell>
                        </TableRow>
                        {hosting.backup && hosting.backup.storage > 0 && (
                            <TableRow>
                                <TableCell>Backup ({hosting.backup.storage} Go)</TableCell>
                                <TableCell>Retention: {hosting.backup.retentionTime}j</TableCell>
                                <TableCell align="right">
                                    {renderPriceCell(hosting.prices.backup, isCompared && prices[0] ? prices[0].backup : hosting.prices.backup)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Paper elevation={0} sx={{ 
                p: 2, 
                borderRadius: '12px', 
                bgcolor: isCompared ? (totalHT > currentTotalHT ? '#fff1f2' : '#f0fdf4') : '#eff6ff',
                border: '1px solid',
                borderColor: isCompared ? (totalHT > currentTotalHT ? '#fecdd3' : '#bbf7d0') : '#dbeafe',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#64748b', textTransform: 'uppercase' }}>
                        Total HT mensuel {isCompared && "(Estimé OVH)"}
                    </Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: getCompareStyle(currentTotalHT, totalHT).color }}>
                        {totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </Typography>
                </Box>
                {isCompared && (
                    <Chip 
                        label={totalHT > currentTotalHT ? "+ Cher" : totalHT < currentTotalHT ? "- Cher" : "Identique"} 
                        color={totalHT > currentTotalHT ? "error" : "success"}
                        size="small"
                        sx={{ fontWeight: 700 }}
                    />
                )}
            </Paper>
        </Box>
    );
}